import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { DialogActionTrigger, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { RadioCardItem, RadioCardRoot } from "@/components/ui/radio-card";
import { toaster } from "@/components/ui/toaster";
import { Flex, Stack, Text, Textarea } from "@chakra-ui/react";

import { LuBug, LuHammer, LuMail, LuMessageCircle, LuSend, LuX } from "react-icons/lu";

import UserApi from "@/api/services/user";

export const FeedbackModal = ({ children, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      classifications: "feature_request",
      feedback: "",
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: (data) => new UserApi().feedback(data),
    onSuccess: () => {
      toaster.create({
        title: "Feedback Sent",
        description: "Thank you for your feedback! We'll use it to improve Plan Harmony.",
        type: "success",
      });
      setIsOpen(false);
      formMethods.reset();
    },
    onError: (error) => {
      console.error("Feedback submission error:", error);
      toaster.create({
        title: "Error",
        description: "There was an error sending your feedback. Please try again.",
        type: "error",
      });
    },
  });

  const onSubmit = (data) => {
    if (data.classifications.length === 0) {
      toaster.create({
        title: "Classification Required",
        description: "Please select at least one classification for your feedback.",
        type: "error",
      });
      return;
    }

    if (!data.feedback.trim()) {
      toaster.create({
        title: "Feedback Required",
        description: "Please provide your feedback in the text area.",
        type: "error",
      });
      return;
    }

    feedbackMutation.mutate({
      classifications: data.classifications,
      feedback: data.feedback.trim(),
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    formMethods.reset();
  };

  const classificationOptions = [
    { value: "feature_request", label: "Feature Request", icon: <LuHammer /> },
    { value: "bug_report", label: "Bug Report", icon: <LuBug /> },
    { value: "general_feedback", label: "General Feedback", icon: <LuMessageCircle /> },
  ];

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size="lg" {...props}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent borderRadius="2xl" boxShadow="0 20px 60px rgba(0,0,0,0.15)" overflow="hidden">
        <DialogHeader pb={3}>
          <DialogTitle>
            <Flex direction="row" align="center" gap={2}>
              <Flex align="center" justify="center" w={8} h={8} bg="gray.100" borderRadius="lg" color="gray.700">
                <LuMail size={18} />
              </Flex>
              <Text fontWeight="700" letterSpacing="-0.01em">
                Share Your Feedback
              </Text>
            </Flex>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <DialogBody py={4}>
            <Stack gap={4}>
              <Field label="What type of feedback is this?" required errorText={formMethods.formState.errors.classifications?.message} invalid={!!formMethods.formState.errors.classifications}>
                <Controller
                  name="classifications"
                  control={formMethods.control}
                  render={({ field }) => (
                    <RadioCardRoot
                      orientation="horizontal"
                      defaultValue="feature_request"
                      value={field.value}
                      w="full"
                      onValueChange={({ value }) => {
                        field.onChange(value);
                      }}>
                      <Flex direction="row" gap={2} flexWrap="wrap">
                        {classificationOptions.map((item) => (
                          <RadioCardItem
                            indicatorPlacement="start"
                            key={item.value}
                            value={item.value}
                            label={item.label}
                            icon={item.icon}
                            cursor="pointer"
                            borderRadius="xl"
                            borderWidth="2px"
                            borderColor="gray.200"
                            _checked={{
                              borderColor: "gray.500",
                              bg: "gray.50",
                            }}
                          />
                        ))}
                      </Flex>
                    </RadioCardRoot>
                  )}
                />
              </Field>
              <Field label="Your feedback" required errorText={formMethods.formState.errors.feedback?.message} invalid={!!formMethods.formState.errors.feedback} helperText="Please share your thoughts on how we can improve Plan Harmony">
                <Textarea
                  {...formMethods.register("feedback", {
                    required: "Please provide your feedback",
                    minLength: {
                      value: 5,
                      message: "Please provide at least 5 characters of feedback",
                    },
                  })}
                  placeholder="Tell us about your experience, suggestions, or any issues you've encountered..."
                  rows={6}
                  resize="vertical"
                  borderRadius="xl"
                  bg="gray.50"
                  borderColor="gray.200"
                  _hover={{ borderColor: "gray.300" }}
                  _focus={{ borderColor: "gray.400", bg: "white" }}
                />
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter gap={2} pt={0}>
            <DialogActionTrigger asChild>
              <Button variant="ghost" onClick={handleClose} borderRadius="xl" fontWeight="600">
                <LuX />
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              loading={feedbackMutation.status === "pending"}
              loadingText="Sending..."
              bg="gray.900"
              color="white"
              fontWeight="600"
              borderRadius="xl"
              _hover={{
                bg: "gray.800",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)">
              <LuSend />
              Send Feedback
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
